import * as React from "react";

export interface VoiceRecorderProps {
  onCancel: () => void;
  onConfirm: (blob: Blob, ext: string) => void;
}

interface VoiceRecorderState {
  loading: boolean;
  durationSeconds: number;
  visualizerData: number[];
}

const MIN_DECIBELS = -45;

const MIME_TYPES = ["audio/webm; codecs=opus", "audio/webm", "audio/ogg; codecs=opus", "audio/mp4", "audio/wav"];

function calculateRMS(data: Uint8Array): number {
  let sumSquares = 0;
  for (let i = 0; i < data.length; i++) {
    const v = (data[i] - 128) / 128;
    sumSquares += v * v;
  }
  return Math.sqrt(sumSquares / data.length);
}

function normalizeRMS(rms: number): number {
  const scaled = Math.pow(rms * 10, 1.5);
  return Math.min(1.0, Math.max(0.01, scaled));
}

function formatSeconds(s: number): string {
  const m = Math.floor(s / 60);
  const rem = s % 60;
  return `${m}:${rem < 10 ? "0" : ""}${rem}`;
}

/* 2px bar + 2px gap = 4px per slot */
const SLOT_PX = 4;

export default class VoiceRecorder extends React.Component<VoiceRecorderProps, VoiceRecorderState> {
  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private audioChunks: Blob[] = [];
  private durationTimer: number | null = null;
  private animFrame: number | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private timeDomainData: Uint8Array<ArrayBuffer> | null = null;
  private confirmed = false;
  private stopped = false;
  private vizRef = React.createRef<HTMLDivElement>();
  private bufferLen = 150;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      durationSeconds: 0,
      visualizerData: Array(150).fill(0),
    };
  }

  componentDidMount(): void {
    /* calculate exact bar count from container width */
    if (this.vizRef.current) {
      this.bufferLen = Math.max(20, Math.floor(this.vizRef.current.clientWidth / SLOT_PX));
      this.setState({ visualizerData: Array(this.bufferLen).fill(0) });
    }
    this.startRecording();
  }

  componentWillUnmount(): void {
    this.cleanup();
  }

  cleanup(): void {
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
      this.animFrame = null;
    }
    if (this.durationTimer) {
      clearInterval(this.durationTimer);
      this.durationTimer = null;
    }
    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
  }

  async startRecording(): Promise<void> {
    this.stopped = false;
    this.confirmed = false;

    try {
      this.stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (err) {
      console.error("Microphone access denied:", err);
      this.props.onCancel();
      return;
    }

    const mimeType = MIME_TYPES.find((t) => MediaRecorder.isTypeSupported(t)) ?? "";
    const recorder = new MediaRecorder(this.stream, mimeType ? { mimeType } : undefined);
    this.mediaRecorder = recorder;
    this.audioChunks = [];

    recorder.ondataavailable = (e: BlobEvent): void => {
      if (e.data.size > 0) {
        this.audioChunks.push(e.data);
      }
    };

    recorder.onstart = (): void => {
      this.setState({ loading: false });
      this.durationTimer = window.setInterval((): void => {
        this.setState((s) => ({ durationSeconds: s.durationSeconds + 1 }));
      }, 1000);
      this.setupAnalyser();
    };

    recorder.onstop = (): void => {
      if (this.stopped) return;
      this.stopped = true;
      this.cleanup();
      if (this.confirmed && this.audioChunks.length > 0) {
        const type = this.audioChunks[0]?.type || mimeType || "audio/webm";
        const ext = type.split("/")[1]?.split(";")[0] ?? "webm";
        const blob = new Blob(this.audioChunks, { type });
        this.props.onConfirm(blob, ext);
      }
    };

    recorder.start();
  }

  setupAnalyser(): void {
    if (!this.stream) return;
    this.audioContext = new AudioContext();
    const source = this.audioContext.createMediaStreamSource(this.stream);
    this.analyser = this.audioContext.createAnalyser();
    this.analyser.minDecibels = MIN_DECIBELS;
    source.connect(this.analyser);
    this.timeDomainData = new Uint8Array(this.analyser.fftSize);
    this.drawFrame();
  }

  drawFrame = (): void => {
    if (!this.analyser || !this.timeDomainData) return;
    this.analyser.getByteTimeDomainData(this.timeDomainData);
    const rms = normalizeRMS(calculateRMS(this.timeDomainData));
    this.setState((s) => {
      const next = [...s.visualizerData, rms];
      if (next.length > this.bufferLen) next.shift();
      return { visualizerData: next };
    });
    this.animFrame = requestAnimationFrame(this.drawFrame);
  };

  handleStop = (): void => {
    this.confirmed = false;
    this.mediaRecorder?.stop();
    this.props.onCancel();
  };

  handleConfirm = (): void => {
    this.confirmed = true;
    this.mediaRecorder?.stop();
  };

  render(): JSX.Element {
    const { loading, durationSeconds, visualizerData } = this.state;

    return (
      <div className="voice-recorder">
        {/* Cancel */}
        <button
          className="voice-controls__cancel"
          type="button"
          aria-label="Abbrechen"
          title="Abbrechen"
          onClick={this.handleStop}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>

        {/* Visualizer — takes all remaining space */}
        <div className="voice-viz" ref={this.vizRef}>
          <div className="voice-viz__bars">
            {visualizerData.map((rms, i) => (
              <div key={i} className="voice-viz__bar" style={{ height: `${Math.min(100, Math.max(8, rms * 100))}%` }} />
            ))}
          </div>
        </div>

        {/* Timer */}
        <div className={`voice-controls__timer${loading ? " voice-controls__timer--loading" : ""}`}>
          {formatSeconds(durationSeconds)}
        </div>

        {/* Confirm / spinner */}
        {loading ? (
          <div className="voice-controls__spinner" aria-label="Startet …">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <style>{`.vs{transform-origin:center;animation:vs-spin .75s step-end infinite}@keyframes vs-spin{8.3%{transform:rotate(30deg)}16.6%{transform:rotate(60deg)}25%{transform:rotate(90deg)}33.3%{transform:rotate(120deg)}41.6%{transform:rotate(150deg)}50%{transform:rotate(180deg)}58.3%{transform:rotate(210deg)}66.6%{transform:rotate(240deg)}75%{transform:rotate(270deg)}83.3%{transform:rotate(300deg)}91.6%{transform:rotate(330deg)}100%{transform:rotate(360deg)}}`}</style>
              <g className="vs">
                <rect x="11" y="1" width="2" height="5" opacity=".14" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(30 12 12)" opacity=".29" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(60 12 12)" opacity=".43" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(90 12 12)" opacity=".57" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(120 12 12)" opacity=".71" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(150 12 12)" opacity=".86" />
                <rect x="11" y="1" width="2" height="5" transform="rotate(180 12 12)" />
              </g>
            </svg>
          </div>
        ) : (
          <button
            className="voice-controls__confirm"
            type="button"
            aria-label="Aufnahme bestätigen"
            title="Bestätigen"
            onClick={this.handleConfirm}
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </button>
        )}
      </div>
    );
  }
}
